// frontend/src/pages/NewUIActiveMatches.jsx
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "../config/axios";

const REFRESH_INTERVAL = 30000; // 30 segundos

// Formatea cuánto tiempo lleva una partida en curso
function elapsed(startTime) {
    if (!startTime) return null;
    const secs = Math.floor((Date.now() - new Date(startTime).getTime()) / 1000);
    if (secs < 60) return `${secs}s`;
    const mins = Math.floor(secs / 60);
    if (mins < 60) return `${mins}m`;
    return `${Math.floor(mins / 60)}h ${mins % 60}m`;
}

function roundLabel(round) {
    if (!round) return null;
    const labels = { 1: 'Final', 2: 'Semifinal', 4: 'Cuartos', 8: 'Octavos' };
    return labels[round] ? labels[round] : `Ronda ${round}`;
}

// Badge de estado
const StatusBadge = ({ status }) => {
    if (status === 'IN_PROGRESS') {
        return (
            <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-extrabold uppercase bg-[#FCE503] text-[#0E2238]">
                <span className="w-1.5 h-1.5 rounded-full bg-[#0E2238] animate-pulse" />
                En juego
            </span>
        );
    }
    return (
        <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-extrabold uppercase border-2 border-[#FCE503] text-[#FCE503]">
            <span className="w-1.5 h-1.5 rounded-full bg-[#FCE503] animate-pulse" />
            Preparando
        </span>
    );
};

// Card de partida
const MatchCard = ({ match }) => {
    const [time, setTime] = useState(() => elapsed(match.startTime));

    useEffect(() => {
        if (!match.startTime) return;
        const id = setInterval(() => setTime(elapsed(match.startTime)), 10000);
        return () => clearInterval(id);
    }, [match.startTime]);

    const isTournament = match.isTournament;
    const [p1, p2] = match.players;

    return (
        <div className="relative rounded-[18px] overflow-hidden bg-[#122436] ring-2 ring-white shadow-[0_12px_40px_rgba(0,0,0,0.35)] transition-transform duration-200 hover:-translate-y-0.5 flex flex-col">
            {/* Header */}
            <div className="px-5 pt-5 pb-3 flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                    {isTournament ? (
                        <>
                            <p className="text-[#FCE503] text-[11px] font-bold uppercase tracking-wider mb-0.5">
                                🏆 Torneo · {match.tournament.game?.name ?? 'Truco'}
                            </p>
                            <p className="text-white font-bold text-base leading-tight truncate">
                                {match.tournament.name}
                            </p>
                        </>
                    ) : (
                        <>
                            <p className="text-white/50 text-[11px] font-bold uppercase tracking-wider mb-0.5">
                                {match.game?.name ?? 'Partida rápida'}
                            </p>
                            <p className="text-white font-bold text-base leading-tight">
                                Partida rápida
                            </p>
                        </>
                    )}
                </div>
                <StatusBadge status={match.status} />
            </div>

            {/* Separador */}
            <div className="mx-5 border-t border-white/10" />

            {/* Jugadores */}
            <div className="px-5 py-4 flex-1 space-y-3">
                {/* VS layout */}
                <div className="flex items-center gap-3">
                    <div className="flex-1 bg-[#0E2238] rounded-lg px-3 py-2.5 min-w-0">
                        {p1.members.length > 0 ? (
                            <>
                                <p className="text-white/50 text-[10px] uppercase tracking-wide mb-1">{p1.label}</p>
                                {p1.members.map((m, i) => (
                                    <p key={i} className="text-white text-sm font-semibold truncate">{m}</p>
                                ))}
                            </>
                        ) : (
                            <p className="text-white text-sm font-semibold truncate">{p1.label}</p>
                        )}
                    </div>

                    <span className="text-white/30 font-black text-sm flex-shrink-0">VS</span>

                    <div className="flex-1 bg-[#0E2238] rounded-lg px-3 py-2.5 min-w-0 text-right">
                        {p2.members.length > 0 ? (
                            <>
                                <p className="text-white/50 text-[10px] uppercase tracking-wide mb-1">{p2.label}</p>
                                {p2.members.map((m, i) => (
                                    <p key={i} className="text-white text-sm font-semibold truncate">{m}</p>
                                ))}
                            </>
                        ) : (
                            <p className="text-white text-sm font-semibold truncate">{p2.label}</p>
                        )}
                    </div>
                </div>

                {/* Info adicional: torneo */}
                {isTournament && (
                    <div className="flex items-center justify-between text-sm">
                        {match.round && (
                            <span className="text-white/60 text-xs font-semibold uppercase tracking-wide">
                                {roundLabel(match.round)}
                            </span>
                        )}
                        <span className="ml-auto font-bold text-[#FCE503]">
                            Premio: {match.tournament.prizePool?.toLocaleString('es-AR')} tickets
                        </span>
                    </div>
                )}

                {/* Timer */}
                {time && (
                    <div className="flex items-center gap-1.5 text-white/40 text-xs">
                        <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {time} en curso
                    </div>
                )}
            </div>

            {/* Footer: botón observar */}
            <div className="relative group">
                <button
                    disabled
                    className="w-full h-10 font-extrabold uppercase tracking-wide border-t border-white/10 bg-[#0a1929] text-white/30 cursor-not-allowed text-sm"
                >
                    Observar
                </button>
                {/* Tooltip */}
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-[#FCE503] text-[#122436] text-xs font-bold rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none shadow-lg">
                    Próximamente disponible
                    <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-[#FCE503]" />
                </div>
            </div>
        </div>
    );
};

// Selector de juego (tab)
const GameSelector = ({ games, selected, onSelect }) => (
    <div className="flex flex-wrap gap-3 justify-center mb-8">
        <button
            onClick={() => onSelect(null)}
            className={`px-6 py-3 rounded-[14px] font-bold tracking-wider uppercase transition-all duration-200 ${
                selected === null
                    ? 'bg-[#FCE503] text-[#122436] shadow-[0_10px_30px_rgba(252,229,3,0.35)]'
                    : 'bg-[#122436] text-white hover:bg-[#1a3654] ring-2 ring-white/40'
            }`}
        >
            Todos
        </button>
        {games.map(g => (
            <button
                key={g.slug}
                onClick={() => onSelect(g.slug)}
                className={`px-6 py-3 rounded-[14px] font-bold tracking-wider uppercase transition-all duration-200 ${
                    selected === g.slug
                        ? 'bg-[#FCE503] text-[#122436] shadow-[0_10px_30px_rgba(252,229,3,0.35)]'
                        : 'bg-[#122436] text-white hover:bg-[#1a3654] ring-2 ring-white/40'
                }`}
            >
                {g.name}
            </button>
        ))}
    </div>
);

const AVAILABLE_GAMES = [
    { slug: 'truco', name: 'Truco', hasPage: true },
    { slug: 'rummy', name: 'Rummy', hasPage: false },
    { slug: 'escoba', name: 'Escoba 15', hasPage: false },
    { slug: 'chinchon', name: 'Chinchón', hasPage: false },
];

const NewUIActiveMatches = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [matches, setMatches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [lastUpdated, setLastUpdated] = useState(null);
    // Inicializar con el ?game= de la URL si viene desde la página de un juego
    const [selectedGame, setSelectedGame] = useState(() => {
        const param = searchParams.get('game');
        return AVAILABLE_GAMES.some(g => g.slug === param) ? param : null;
    });
    const [typeFilter, setTypeFilter] = useState('all'); // all | tournament | quick

    useEffect(() => {
        document.body.classList.add('acumin-scope');
        document.body.style.overflowX = 'hidden';
        return () => {
            document.body.classList.remove('acumin-scope');
            document.body.style.overflowX = '';
        };
    }, []);

    const fetchMatches = useCallback(async (silent = false) => {
        try {
            if (!silent) setLoading(true);
            const res = await axios.get('/matches/active');
            if (res.data.success) {
                setMatches(res.data.matches);
                setLastUpdated(new Date());
                setError('');
            }
        } catch (err) {
            if (!silent) setError('No se pudieron cargar las partidas activas.');
        } finally {
            if (!silent) setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchMatches();
        const id = setInterval(() => fetchMatches(true), REFRESH_INTERVAL);
        return () => clearInterval(id);
    }, [fetchMatches]);

    // Filtrar por juego y tipo
    const filtered = matches.filter(m => {
        const gameSlug = m.game?.slug ?? null;
        if (selectedGame && gameSlug !== selectedGame) return false;
        if (typeFilter === 'tournament' && !m.isTournament) return false;
        if (typeFilter === 'quick' && m.isTournament) return false;
        return true;
    });

    const selectedGameData = AVAILABLE_GAMES.find(g => g.slug === selectedGame);

    return (
        <div className="min-h-screen overflow-x-hidden">
            <section
                className="relative overflow-hidden text-white min-h-screen py-16 md:py-24"
                style={{ backgroundColor: "#2284DF" }}
            >
                {/* Textura */}
                <div
                    className="absolute inset-0 pointer-events-none z-0"
                    style={{
                        backgroundColor: "#1F79CF",
                        WebkitMaskImage: "url(/images/backgrounds/textura-azul.png)",
                        maskImage: "url(/images/backgrounds/textura-azul.png)",
                        WebkitMaskRepeat: "repeat",
                        maskRepeat: "repeat",
                        WebkitMaskSize: "1769px 1183px",
                        maskSize: "1769px 1183px",
                    }}
                    aria-hidden="true"
                />

                <div className="relative z-10 max-w-[calc(100%-100px)] mx-auto px-4 sm:px-6 lg:px-8">
                    {/* Título */}
                    <div className="text-center mb-10 pt-16 md:pt-20">
                        <h1
                            className="toon toon-3d text-4xl sm:text-5xl md:text-6xl font-extrabold uppercase text-white mb-4"
                            style={{
                                WebkitTextStroke: "1px #000",
                                textShadow: "0 2px 0 rgba(0,0,0,1), 0 4px 2px rgba(0,0,0,1)",
                            }}
                        >
                            Partidas Activas
                        </h1>
                        <p className="text-white/90 text-lg md:text-xl tracking-wider max-w-3xl mx-auto">
                            SEGUÍ LAS PARTIDAS EN VIVO DE LA COMUNIDAD
                        </p>
                    </div>

                    {/* Tabs de juego */}
                    <GameSelector
                        games={AVAILABLE_GAMES}
                        selected={selectedGame}
                        onSelect={setSelectedGame}
                    />

                    {/* Filtro torneo / rápida */}
                    <div className="flex justify-center gap-3 mb-8">
                        {[
                            { value: 'all', label: 'Todas' },
                            { value: 'tournament', label: '🏆 Torneos' },
                            { value: 'quick', label: '⚡ Rápidas' },
                        ].map(opt => (
                            <button
                                key={opt.value}
                                onClick={() => setTypeFilter(opt.value)}
                                className={`px-5 py-2 rounded-lg font-semibold text-sm uppercase tracking-wide transition-colors ${
                                    typeFilter === opt.value
                                        ? 'bg-white text-[#122436]'
                                        : 'bg-white/10 text-white/80 hover:bg-white/20'
                                }`}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>

                    {/* Barra de estado: contador + último update + refresh */}
                    <div className="flex items-center justify-between mb-6">
                        <p className="text-white/80 text-sm">
                            {loading ? 'Cargando...' : (
                                <>
                                    <span className="font-bold text-[#FCE503]">{filtered.length}</span> partida{filtered.length !== 1 ? 's' : ''} activa{filtered.length !== 1 ? 's' : ''}
                                    {matches.length !== filtered.length && (
                                        <span className="text-white/50"> · {matches.length} en total</span>
                                    )}
                                </>
                            )}
                        </p>
                        <div className="flex items-center gap-3">
                            {lastUpdated && (
                                <span className="text-white/40 text-xs hidden sm:block">
                                    Actualizado {lastUpdated.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}
                                </span>
                            )}
                            <button
                                onClick={() => fetchMatches()}
                                disabled={loading}
                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white text-xs font-semibold transition-colors disabled:opacity-50"
                            >
                                <svg className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                </svg>
                                Actualizar
                            </button>
                        </div>
                    </div>

                    {/* Contenido principal */}
                    {error ? (
                        <div className="text-center py-16">
                            <div className="bg-[#122436] rounded-2xl p-10 inline-block ring-2 ring-white/20">
                                <p className="text-white/70 mb-4">{error}</p>
                                <button
                                    onClick={() => fetchMatches()}
                                    className="px-6 py-2.5 bg-[#FCE503] text-[#122436] font-bold rounded-lg uppercase tracking-wide hover:bg-[#FCE503]/90 transition-colors"
                                >
                                    Reintentar
                                </button>
                            </div>
                        </div>
                    ) : loading ? (
                        <div className="grid gap-6 md:gap-8 [grid-template-columns:repeat(auto-fill,minmax(320px,1fr))]">
                            {[...Array(6)].map((_, i) => (
                                <div key={i} className="rounded-[18px] bg-[#122436]/60 ring-2 ring-white/20 min-h-[220px] animate-pulse" />
                            ))}
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="text-center py-20">
                            <div className="bg-[#122436] rounded-2xl p-12 inline-block ring-2 ring-white/20 max-w-md mx-auto">
                                <div className="text-5xl mb-4">🃏</div>
                                <p className="text-white font-bold text-lg mb-2">No hay partidas activas</p>
                                <p className="text-white/50 text-sm mb-6">
                                    {typeFilter !== 'all' || selectedGame
                                        ? 'Probá con otros filtros o revisá más tarde.'
                                        : 'Las partidas aparecerán aquí cuando comiencen.'}
                                </p>
                                {(typeFilter !== 'all' || selectedGame) && (
                                    <button
                                        onClick={() => { setTypeFilter('all'); setSelectedGame(null); }}
                                        className="px-6 py-2.5 bg-[#FCE503] text-[#122436] font-bold rounded-lg uppercase tracking-wide hover:bg-[#FCE503]/90 transition-colors text-sm"
                                    >
                                        Limpiar filtros
                                    </button>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="grid gap-6 md:gap-8 [grid-template-columns:repeat(auto-fill,minmax(300px,1fr))]">
                            {filtered.map(m => (
                                <MatchCard key={m.id} match={m} />
                            ))}
                        </div>
                    )}

                    {/* Botón ir a jugar */}
                    <div className="mt-14 flex justify-center">
                        <button
                            onClick={() => {
                                const game = selectedGameData ?? AVAILABLE_GAMES[0];
                                navigate(game.hasPage ? `/games/${game.slug}` : '/next-games');
                            }}
                            className="inline-flex items-center justify-center min-w-[260px] md:min-w-[420px] h-12 md:h-14 px-8 rounded-[16px] font-bold tracking-wider uppercase text-[#122436] bg-[#FCE503] hover:bg-[#FCE503]/90 transition-colors shadow-[0_10px_30px_rgba(252,229,3,0.35)]"
                        >
                            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            Jugar {selectedGameData?.name ?? 'Truco'}
                        </button>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default NewUIActiveMatches;
